<template>
  <v-container>
    <v-dialog :value="!!editingApp" max-width="700px" @input="editingApp = null" persistent>
      <QlikAppEditor v-if="editingApp" :app="editingApp" @confirm="update" @cancel="editingApp = null"/>
    </v-dialog>
    <ConfirmDialog 
      :show="!!askToRemoveApp" 
      :description="$t('QlikApp.Message.disconnectWarning')"
      @cancel="askToRemoveApp = null" 
      @confirm="remove" 
    />
    <v-row align="start">
      <!-- ===== HEADER ===== -->
      <v-col cols="12">
        <v-toolbar color="transparent" flat class="mb-10">
          <v-toolbar-title class="text-xl-h4 text-lg-h5 font-weight-bold">
            {{ $t('QlikApp.Header.title') }}
          </v-toolbar-title>
        </v-toolbar>
      </v-col>
      <v-slide-x-transition>
        <v-col cols="12" v-if="qlikAppsFetched">
          <v-slide-x-transition group tag="div" class="row">
            <v-col v-for="app in linkedApps" :key="'app' + app.id" cols="12" lg="6" xl="4">
              <QlikAppCard :app="app" @settings="editingApp = app" @disconnect="askToRemoveApp = app" />
            </v-col>
          </v-slide-x-transition>
        </v-col>
      </v-slide-x-transition>
      <v-slide-x-transition>
        <v-col cols="12" v-if="qlikApps.length" class="mt-10">
          <v-slide-x-transition group tag="div" class="row" leave-absolute hide-on-leave>
            <v-col cols="12" key="toolbar-qap">
              <v-toolbar color="transparent" flat>
                <v-toolbar-title>
                  <v-icon large left color="black">apps</v-icon>
                  Qlik Analytics Platform
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <SearchField @search="value => search = value"/>
              </v-toolbar>
            </v-col>
            <v-col v-for="app in unlinkedApps" :key="'app' + app.qDocId" cols="12" md="6" lg="4" xl="3">
              <QlikRawAppCard :qDoc="app" @connect="add(app)"/>
            </v-col>
          </v-slide-x-transition>
        </v-col>
      </v-slide-x-transition>
    </v-row>
  </v-container>
</template>

<script>
import { QlikAppRepository } from '@portal/api'
import { getApps } from '@portal/qlik'
import { mapState } from 'vuex'
import QlikAppCard from '@/components/Qlik/QlikAppCard'
import QlikRawAppCard from '@/components/Qlik/QlikRawAppCard'
import QlikAppEditor from '@/components/Qlik/QlikAppEditor'
import SearchField from '@/components/Utils/SearchField'
import ConfirmDialog from '@/components/Utils/ConfirmDialog'

export default {
  components: { QlikAppCard, QlikRawAppCard, QlikAppEditor, SearchField, ConfirmDialog },
  data: () => ({
    portalApps: [],
    qlikApps: [],
    search: '',
    editingApp: null,
    qlikAppsFetched: false,
    askToRemoveApp: null
  }),
  computed: {
    ...mapState('qlik', [
      'isConnected'
    ]),
    linkedApps () {
      return this.portalApps
        .map(app => ({
          ...app,
          qDoc: this.qlikApps.find(qlikApp => app.qAppId === qlikApp.qDocId)
        }))
    },
    unlinkedApps () {
      return this.qlikApps
        .filter(qlikApp => 
          !this.portalApps.some(app => app.qAppId === qlikApp.qDocId) && 
          qlikApp.qDocName.toLowerCase().match(this.search.toLowerCase())
        )
    }
  },
  watch: {
    isConnected: {
      immediate: true,
      handler: async function (isConnected) {
        if (isConnected) {
          this.qlikApps = await getApps()
          this.qlikAppsFetched = true
        }
      }
    }
  },
  methods: {
    async fetch () {
      try {
        this.portalApps = await QlikAppRepository.fetchAll()
      } catch {
        this.$store.dispatch('notify/error', this.$t('Message.Error.read'))
      }
    },
    async add (qlikApp) {
      try {
        const newApp = await QlikAppRepository.create({
          name: qlikApp.qDocName,
          qAppId: qlikApp.qDocId,
          icon: 'analytics',
          isEnabled: true,
          color: this.$vuetify.theme.themes.light.primary
        })
        this.portalApps.push(newApp)
        this.$store.dispatch('qlik/loadApps')

      } catch {
        this.$store.dispatch('notify/error', this.$t('Message.Error.create'))
      }
    },
    async update (app) {
      try {
        const updatedApp = await QlikAppRepository.update(this.editingApp.id, app)
        const index = this.portalApps.findIndex(portalApp => portalApp.id === this.editingApp.id)
        this.portalApps.splice(index, 1, updatedApp)
        this.editingApp = null
        this.$store.dispatch('qlik/loadApps')
      } catch {
        this.$store.dispatch('notify/error', this.$t('Message.Error.update'))
      }
    },
    async remove () {
      try {
        await QlikAppRepository.delete(this.askToRemoveApp.id)
        const index = this.portalApps.findIndex(portalApp => portalApp.id === this.askToRemoveApp.id)
        this.portalApps.splice(index, 1)
        this.askToRemoveApp = null
        this.$store.dispatch('qlik/loadApps')
      } catch {
        this.$store.dispatch('notify/error', this.$t('Message.Error.delete'))
      }
    }
  },
  async created () {
    await this.fetch()
  }
}
</script>